/**
 * Dashboard — compliance-monitoring home page for a website.
 *
 * Answers the user's main questions:
 *   1. What is my current accessibility status?
 *   2. Has it improved or worsened?
 *   3. What should I do next?
 *   4. When was the last scan?
 *
 * Data sources (all existing, no new API calls):
 *   - useScore          → latest score run summary
 *   - useLatestReport   → latest report summary (coverage stats, issue counts)
 *   - useLatestRecurring → latest recurring comparison (score delta, new/resolved)
 *   - getLatestExecution → latest pipeline execution (status, timestamps)
 *   - score history      → chart data
 */

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useScore } from "../hooks/use-score.js";
import { useLatestReport, useReportPayload } from "../hooks/use-report.js";
import { useLatestRecurring } from "../hooks/use-recurring.js";
import { useFirstOrg } from "../hooks/use-orgs.js";
import { useWebsites } from "../hooks/use-websites.js";
import { useBillingSummary } from "../hooks/use-billing.js";
import { usePostScanRefresh } from "../hooks/use-post-scan-refresh.js";
import { useFeatureReadiness } from "../hooks/use-feature-readiness.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { ScoreGauge } from "../components/score-gauge.js";
import { ScoreHistoryChart } from "../components/score-history-chart.js";
import { ScanDiagnosticsPanel } from "../components/scan-diagnostics-panel.js";
import { WarningPanel } from "../components/warning-panel.js";
import { RunStatusBadge } from "../components/run-status-badge.js";
import { PageSkeleton } from "../components/skeleton.js";
import { ComplianceTimeline } from "../components/compliance-timeline.js";
import { buildDiagnosticSummary, translateDegradedFeatures } from "../lib/diagnostics.js";
import { apiGet, ApiCallError } from "../api/client.js";
import type { ScoreHistoryEntry } from "../api/types.js";
import type { ScoreBand } from "../api/product-types.js";

/* ── Helpers ────────────────────────────────────────────────────────────────── */

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString("en-IE");
}

function scoreBandExplanation(band: ScoreBand): string {
  switch (band) {
    case "strong":
      return "Automated checks found few accessibility issues. This is a good baseline, but manual testing is still recommended for full coverage.";
    case "needs_improvement":
      return "Several accessibility issues were detected. Review the report for remediation guidance.";
    case "accessibility_risk":
      return "Significant accessibility issues were found. Users with disabilities may face barriers on this site.";
    case "high_risk":
      return "Critical accessibility issues were found. Immediate attention is recommended.";
  }
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export function DashboardPage() {
  const { websiteId } = useParams<{ websiteId: string }>();

  const { data: score, isLoading: scoreLoading, isError: scoreError } = useScore(websiteId);
  const { data: report, isLoading: reportLoading, isError: reportError } = useLatestReport(websiteId);
  const { data: reportPayload } = useReportPayload(websiteId, report?.id);
  const { data: recurring, isLoading: recurringLoading } =
    useLatestRecurring(websiteId);

  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const website =
    websitesData?.websites.find((w) => w.id === websiteId) ?? null;
  const { data: billingData } = useBillingSummary(org?.id);
  const limits = billingData?.summary?.limits;

  const { execution, isRunning } = usePostScanRefresh(websiteId, org?.id);

  // Feature readiness + diagnostics
  const { data: features } = useFeatureReadiness();
  const degradedFeatures = features ? translateDegradedFeatures(features) : [];
  const scanOutcome = reportPayload?.scanOutcome ?? null;
  const screenshotsAvailable =
    (reportPayload?.truthfulness?.screenshotsCaptured ?? 0) > 0;
  const diagnosticSummary = buildDiagnosticSummary(
    scanOutcome,
    screenshotsAvailable,
    degradedFeatures,
  );

  const { data: historyData } = useQuery({
    queryKey: ["scores", "history", websiteId],
    queryFn: async () => {
      try {
        return await apiGet<{ history: ScoreHistoryEntry[] }>(
          `/websites/${websiteId}/scores/history`,
        );
      } catch (err) {
        if (err instanceof ApiCallError && err.status === 404)
          return { history: [] };
        throw err;
      }
    },
    enabled: !!websiteId,
    staleTime: 5 * 60 * 1000,
  });

  const loading = scoreLoading || reportLoading || recurringLoading;

  const allWarnings = [
    ...(score?.warnings ?? []),
    ...(report?.warnings ?? []),
    ...(recurring?.warnings ?? []),
  ];

  // Detect stale score: the latest pipeline failed or was partial, and the
  // displayed score is from a previous (older) pipeline run.
  const isScoreStale =
    score != null &&
    execution != null &&
    !isRunning &&
    (execution.status === "failed" || execution.status === "partial") &&
    score.startedAt < (execution.startedAt ?? "");

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: website?.domain ?? "Website" },
        ]}
      />
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">
        Accessibility monitoring overview for this website.
      </p>

      {loading && <PageSkeleton />}

      {!loading && (scoreError || reportError) && (
        <div className="alert alert--error" role="alert" style={{ marginBottom: "1rem" }}>
          Failed to load some dashboard data. Try refreshing the page.
        </div>
      )}

      {/* ── Scan in progress banner ──────────────────────────────── */}
      {!loading && isRunning && execution && (
        <section className="product-section" aria-live="polite" role="status">
          <div className="alert alert--info" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <span className="spinner" aria-hidden="true" style={{
              display: "inline-block",
              width: "1.25rem",
              height: "1.25rem",
              border: "2px solid currentColor",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              flexShrink: 0,
            }} />
            <div>
              <strong>Scan in progress</strong>
              <span className="text-sm" style={{ marginLeft: "0.5rem" }}>
                Started {formatTimeAgo(execution.startedAt)}
                {execution.stages && execution.stages.length > 0 && (
                  <> &mdash; {execution.stages.filter(s => s.status === "success").length}/{execution.stages.length} stages complete</>
                )}
              </span>
              <p className="text-muted text-sm" style={{ margin: "0.25rem 0 0 0" }}>
                Results will update automatically when the scan finishes. This typically takes 2-10 minutes.
              </p>
            </div>
          </div>
        </section>
      )}

      {!loading && (
        <>
          {/* ── Status summary ──────────────────────────────────────────── */}
          <section className="product-section" aria-labelledby="status-heading">
            <h2 id="status-heading" className="section-title">
              Current status
            </h2>

            {score ? (
              <>
                <ScoreGauge
                  scoreValue={score.scoreValue}
                  scoreBand={score.scoreBand}
                  confidence={score.confidence}
                  pagesScanned={score.pagesScanned}
                />
                {score.pagesScanned === 0 && (
                  <div className="alert alert--error mt-2" role="alert">
                    The last scan could not analyse any pages. Your site may be
                    blocking automated access. Check the{" "}
                    <Link to={`/websites/${websiteId}/report`}>report</Link>{" "}
                    for details, or review your{" "}
                    <Link to={`/websites/${websiteId}/settings`}>
                      scanner allowlisting settings
                    </Link>.
                  </div>
                )}
                {score.pagesScanned > 0 && score.scoreBand && (
                  <p className="text-muted text-sm mt-3">
                    {scoreBandExplanation(score.scoreBand)}
                  </p>
                )}
                {score.pagesScanned > 0 && (
                  <p
                    className="text-muted text-sm mt-2"
                    style={{ fontStyle: "italic" }}
                  >
                    Automated checks identify common accessibility issues, but
                    manual review is required for a complete assessment.
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted">
                No scan results yet.{" "}
                <Link to={`/websites/${websiteId}/scan`}>
                  Run your first scan
                </Link>{" "}
                to see your accessibility status.
              </p>
            )}

            {score?.status === "partial" && (
              <div className="alert alert--warning mt-2" role="status">
                Score computed from partial data — some pages could not be
                scanned. Results may be incomplete.
              </div>
            )}
            {score?.status === "failed" && (
              <div className="alert alert--error mt-2" role="alert">
                Last scan failed.{" "}
                <Link to={`/websites/${websiteId}/scan`}>
                  Run a new scan
                </Link>{" "}
                to get a fresh score.
              </div>
            )}
            {score?.confidence === "low" && score?.status === "success" && (
              <div className="alert alert--warning mt-2" role="status">
                Score confidence is low — fewer pages were scanned than
                expected. A larger scan depth will produce more reliable
                results.
              </div>
            )}
            {isScoreStale && (
              <div className="alert alert--warning mt-2" role="status">
                Your latest scan did not complete successfully. The score shown
                may be from a previous scan.
              </div>
            )}
          </section>

          {/* ── Operator diagnostics ───────────────────────────────────── */}
          {!isRunning && (score || report) && (
            <section className="product-section" aria-labelledby="diagnostics-heading">
              <h2 id="diagnostics-heading" className="section-title">
                Scan health
              </h2>
              <ScanDiagnosticsPanel
                summary={diagnosticSummary}
                variant="compact"
              />
            </section>
          )}

          {/* ── What to do next ─────────────────────────────────────── */}
          <NextActionSection
            websiteId={websiteId!}
            hasScore={!!score}
            scoreFailed={score?.status === "failed"}
            scoreBand={score?.scoreBand ?? null}
            isRunning={!!isRunning}
            hasReport={!!report}
            issueGroupCount={score?.issueGroupCount ?? 0}
            criticalCount={score?.criticalCount ?? 0}
            seriousCount={score?.seriousCount ?? 0}
          />

          {/* ── Scan freshness ─────────────────────────────────────────── */}
          {execution && (
            <section className="product-section" aria-labelledby="freshness-heading">
              <h2 id="freshness-heading" className="section-title">
                Last scan
              </h2>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-item__value">
                    <RunStatusBadge status={execution.status as "success" | "partial" | "failed" | "running" | "idle" | "skipped"} />
                  </span>
                  <span className="stat-item__label">Status</span>
                </div>
                <div className="stat-item">
                  <span className="stat-item__value">
                    {formatTimeAgo(execution.completedAt ?? execution.startedAt)}
                  </span>
                  <span className="stat-item__label">
                    {execution.completedAt ? "Completed" : "Started"}
                  </span>
                </div>
                {score && (
                  <>
                    <div className="stat-item">
                      <span className="stat-item__value">
                        {score.pagesScanned}
                        {report && report.pagesDiscovered > 0 && (
                          <span className="text-muted text-sm fw-normal">
                            {" "}/ {report.pagesDiscovered}
                          </span>
                        )}
                      </span>
                      <span className="stat-item__label">Pages scanned</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-item__value">
                        {score.issueGroupCount}
                      </span>
                      <span className="stat-item__label">Issue groups</span>
                    </div>
                  </>
                )}
              </div>
          </section>
        )}

          {/* ── Top issues ─────────────────────────────────────────────── */}
          {score && (score.criticalCount > 0 || score.seriousCount > 0) && (
            <section className="product-section" aria-labelledby="top-issues-heading">
              <h2 id="top-issues-heading" className="section-title">
                Top priorities
              </h2>
              <div className="stat-grid">
                {score.criticalCount > 0 && (
                  <div className="stat-item stat-item--danger">
                    <span className="stat-item__value">
                      {score.criticalCount}
                    </span>
                    <span className="stat-item__label">Critical issues</span>
                  </div>
                )}
                {score.seriousCount > 0 && (
                  <div className="stat-item stat-item--warning">
                    <span className="stat-item__value">
                      {score.seriousCount}
                    </span>
                    <span className="stat-item__label">Serious issues</span>
                  </div>
                )}
                <div className="stat-item">
                  <span className="stat-item__value">
                    {score.moderateCount}
                  </span>
                  <span className="stat-item__label">Moderate</span>
                </div>
                <div className="stat-item">
                  <span className="stat-item__value">
                    {score.minorCount}
                  </span>
                  <span className="stat-item__label">Minor</span>
                </div>
              </div>
              <p className="mt-3">
                <Link
                  to={`/websites/${websiteId}/report`}
                  className="btn btn--secondary btn--sm"
                >
                  View full report with remediation guidance
                </Link>
              </p>
            </section>
          )}

          {/* ── Score trend ────────────────────────────────────────────── */}
          {(historyData?.history?.length ?? 0) >= 2 && (
          <section
            className="product-section"
            aria-labelledby="history-heading"
          >
            <h2 id="history-heading" className="section-title">
              Score trend
            </h2>
            <ScoreHistoryChart history={historyData?.history ?? []} />
          </section>
          )}

          {/* ── Changes since last scan ────────────────────────────────── */}
          {recurring && (
            <section
              className="product-section"
              aria-labelledby="changes-heading"
            >
              <h2 id="changes-heading" className="section-title">
                Changes since last scan
              </h2>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-item__value">
                    {recurring.issueGroupsNew}
                  </span>
                  <span className="stat-item__label">New issues</span>
                </div>
                <div className="stat-item">
                  <span className="stat-item__value">
                    {recurring.issueGroupsResolved}
                  </span>
                  <span className="stat-item__label">Resolved</span>
                </div>
                {recurring.scoreDelta != null && (
                  <div className="stat-item">
                    <span
                      className={`stat-item__value ${recurring.scoreDelta >= 0 ? "text-success" : "text-danger"}`}
                    >
                      {recurring.scoreDelta >= 0 ? "+" : ""}
                      {recurring.scoreDelta.toFixed(1)}
                    </span>
                    <span className="stat-item__label">Score change</span>
                  </div>
                )}
              </div>
              <Link
                to={`/websites/${websiteId}/recurring`}
                className="btn btn--secondary btn--sm mt-2"
              >
                View monitoring details
              </Link>
            </section>
          )}

          {/* ── Coverage ───────────────────────────────────────────────── */}
          {report && (
            <section
              className="product-section"
              aria-labelledby="coverage-heading"
            >
              <h2 id="coverage-heading" className="section-title">
                Scan coverage
                {report.pagesDiscovered > 0 && (
                  <span
                    className={`text-sm fw-normal ${
                      report.pagesScanned === 0
                        ? "text-danger"
                        : report.pagesScanned < report.pagesDiscovered * 0.5
                          ? "text-warning"
                          : "text-muted"
                    }`}
                    style={{ marginLeft: "0.5rem" }}
                  >
                    {Math.min(
                      100,
                      Math.round(
                        (report.pagesScanned / report.pagesDiscovered) * 100,
                      ),
                    )}
                    %
                  </span>
                )}
              </h2>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-item__value">
                    {report.pagesScanned}{" "}
                    <span className="text-muted text-sm fw-normal">
                      / {report.pagesDiscovered}
                    </span>
                  </span>
                  <span className="stat-item__label">Pages scanned</span>
                </div>
                {report.templatesDetected > 0 && (
                  <div className="stat-item">
                    <span className="stat-item__value">
                      {report.templatesDetected}
                    </span>
                    <span className="stat-item__label">Templates detected</span>
                  </div>
                )}
              </div>
              {report.pagesScanned === 0 && report.pagesDiscovered > 0 && (
                <div className="alert alert--error mt-2" role="alert">
                  No pages could be scanned. The site may be blocking automated
                  access, or pages failed to load. See the{" "}
                  <Link to={`/websites/${websiteId}/report`}>report</Link> for
                  details on what limited this scan.
                </div>
              )}
              {report.pagesScanned > 0 &&
                report.pagesScanned < report.pagesDiscovered && (
                <p className="text-muted text-sm mt-2">
                  {report.pagesScanned} of {report.pagesDiscovered} discovered
                  pages were scanned. The scanner tests representative pages
                  from each template group rather than every page. View the{" "}
                  <Link to={`/websites/${websiteId}/report`}>full report</Link>{" "}
                  for a detailed coverage breakdown and diagnostics.
                </p>
              )}
            </section>
          )}

          {/* ── Warnings ───────────────────────────────────────────────── */}
          {allWarnings.length > 0 && (
            <section className="product-section">
              <WarningPanel
                warnings={allWarnings}
                label="Data quality notices"
              />
            </section>
          )}

          {/* ── Transparency — how we analysed your site ────────────── */}
          {(score || report) && (
            <section className="product-section">
              <details>
                <summary className="section-title cursor-pointer">
                  How we analysed your site
                </summary>
                <div className="mt-3">
                  <p className="text-sm mb-2">
                    <strong>Tools used:</strong> Axe-core, PA11y, and custom
                    accessibility checks run in a headless Chromium browser.
                  </p>
                  {report && (
                    <p className="text-sm mb-2">
                      <strong>Pages scanned:</strong>{" "}
                      {report.pagesScanned} of {report.pagesDiscovered} discovered
                      pages.
                      {report.pagesScanned < report.pagesDiscovered && (
                        <span className="text-muted">
                          {" "}Representative pages were sampled from each
                          template group. See the full report for a detailed
                          coverage breakdown.
                        </span>
                      )}
                    </p>
                  )}
                  {report && report.templatesDetected > 0 && (
                    <p className="text-sm mb-2">
                      <strong>Templates detected:</strong>{" "}
                      {report.templatesDetected} page templates identified.
                    </p>
                  )}
                  <p className="text-sm text-muted mb-3">
                    Automated checks identify common accessibility issues, but
                    manual review is required for a complete assessment.
                  </p>
                  <div className="action-links">
                    <Link
                      to={`/websites/${websiteId}/report`}
                      className="btn btn--secondary btn--sm"
                    >
                      View detailed report
                    </Link>
                    <Link
                      to={`/websites/${websiteId}/scan`}
                      className="btn btn--secondary btn--sm"
                    >
                      Run a new scan
                    </Link>
                  </div>
                </div>
              </details>
            </section>
          )}

          {/* ── Scan setup ──────────────────────────────────────────────── */}
          {website && (
            <section
              className="product-section"
              aria-labelledby="scan-setup-heading"
            >
              <h2 id="scan-setup-heading" className="section-title">
                Scan setup
              </h2>
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-item__value">{website.scanDepth}</span>
                  <span className="stat-item__label">Max pages per scan</span>
                </div>
                <div className="stat-item">
                  <span className="stat-item__value" style={{ textTransform: "capitalize" }}>
                    {website.scanFrequency === "manual"
                      ? "Manual only"
                      : website.scanFrequency}
                  </span>
                  <span className="stat-item__label">Frequency</span>
                </div>
                {limits && !limits.screenshotsEnabled && (
                  <div className="stat-item">
                    <span className="stat-item__value text-muted">No</span>
                    <span className="stat-item__label">Visual evidence</span>
                  </div>
                )}
              </div>
              <p className="text-muted text-sm mt-2">
                <Link to={`/websites/${websiteId}/settings`}>
                  Change scan settings
                </Link>
              </p>
            </section>
          )}

          {/* ── Compliance timeline ──────────────────────────────────── */}
          {websiteId && (
            <section className="product-section">
              <details>
                <summary className="section-title cursor-pointer">
                  Compliance timeline
                </summary>
                <div className="mt-3">
                  <ComplianceTimeline websiteId={websiteId} />
                </div>
              </details>
            </section>
          )}

          {/* ── Quick links ────────────────────────────────────────────── */}
          <section
            className="product-section"
            aria-labelledby="links-heading"
          >
            <h2 id="links-heading" className="section-title">
              Quick links
            </h2>
            <div className="action-links">
              <Link
                to={`/websites/${websiteId}/report`}
                className="btn btn--secondary"
              >
                View report
              </Link>
              <Link
                to={`/websites/${websiteId}/recurring`}
                className="btn btn--secondary"
              >
                View monitoring
              </Link>
              <Link
                to={`/websites/${websiteId}/statement`}
                className="btn btn--secondary"
              >
                View statement
              </Link>
              <Link
                to={`/websites/${websiteId}/scan`}
                className="btn btn--primary"
              >
                Run scan
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

/* ── Next-best-action component ─────────────────────────────────────────────── */

function NextActionSection({
  websiteId,
  hasScore,
  scoreFailed,
  scoreBand,
  isRunning,
  hasReport,
  issueGroupCount,
  criticalCount,
  seriousCount,
}: {
  websiteId: string;
  hasScore: boolean;
  scoreFailed: boolean;
  scoreBand: ScoreBand | null;
  isRunning: boolean;
  hasReport: boolean;
  issueGroupCount: number;
  criticalCount: number;
  seriousCount: number;
}) {
  let message: string;
  let linkTo: string;
  let linkLabel: string;

  if (isRunning) {
    return (
      <section className="product-section" aria-labelledby="next-action-heading">
        <h2 id="next-action-heading" className="section-title">
          What to do next
        </h2>
        <p className="text-muted">
          A scan is in progress. Results will appear here automatically when
          it finishes.
        </p>
      </section>
    );
  }

  if (!hasScore) {
    message = "Run your first accessibility scan to see how your website performs.";
    linkTo = `/websites/${websiteId}/scan`;
    linkLabel = "Run first scan";
  } else if (scoreFailed) {
    message = "The last scan failed to produce a score. Try running a new scan.";
    linkTo = `/websites/${websiteId}/scan`;
    linkLabel = "Run a new scan";
  } else if (criticalCount > 0) {
    message = `${criticalCount} critical ${criticalCount === 1 ? "issue" : "issues"} found. Critical issues often affect the most users — review the report for remediation guidance.`;
    linkTo = `/websites/${websiteId}/report`;
    linkLabel = "View top issues to improve your score";
  } else if (seriousCount > 0) {
    message = `${seriousCount} serious ${seriousCount === 1 ? "issue" : "issues"} found. These should be addressed to improve accessibility.`;
    linkTo = `/websites/${websiteId}/report`;
    linkLabel = "View top issues to improve your score";
  } else if (issueGroupCount > 0 && hasReport) {
    message = `${issueGroupCount} issue ${issueGroupCount === 1 ? "group" : "groups"} found, none critical or serious. Review the report for details.`;
    linkTo = `/websites/${websiteId}/report`;
    linkLabel = "View report";
  } else if (scoreBand === "strong") {
    message = "Automated checks look good. Consider scheduling recurring scans to monitor for regressions.";
    linkTo = `/websites/${websiteId}/recurring`;
    linkLabel = "View monitoring";
  } else {
    message = "Review your latest report for detailed findings and remediation guidance.";
    linkTo = `/websites/${websiteId}/report`;
    linkLabel = "View report";
  }

  return (
    <section className="product-section" aria-labelledby="next-action-heading">
      <h2 id="next-action-heading" className="section-title">
        What to do next
      </h2>
      <p className="text-muted">{message}</p>
      <p className="mt-2">
        <Link to={linkTo} className="btn btn--primary btn--sm">
          {linkLabel}
        </Link>
      </p>
    </section>
  );
}
